// @ts-check
import React from 'react';
import {
  TextInputGroup,
  TextInputGroupMain,
  TextInputGroupUtilities,
  Chip,
  ChipGroup,
  MenuToggle,
  Button
} from '@patternfly/react-core';
import _ from 'lodash';

import classNames from 'classnames';
import styles from './SelectV2.module.css';
import { useTranslation } from 'localization/useTranslation';
import { Icons } from 'components/Common';

/**
 * @typedef {Object} PlaceholderProps
 * @prop {string} value
 */

/**
 * Text styled as a placeholder
 * @param {PlaceholderProps} props
 * @return {JSX.Element}
 */
const Placeholder = ({ value }) => {
  const T = useTranslation();
  return (<span className={styles.placeholder}>{ T.localize(value) }</span>);
};

const SingleMenuToggle = ({ toggleRef, selected, isOpen, isDisabled, onToggleClick, placeholder }) => {
  const firstSelected = _.first(selected);

  return (
    <MenuToggle
      ref={toggleRef}
      onClick={onToggleClick}
      isExpanded={isOpen}
      isDisabled={isDisabled}
      className={classNames(styles.menuToggle)}
      // style={{ width: '100%', backgroundColor: 'white' }}
    >
      { firstSelected ? firstSelected.text : <Placeholder value={placeholder} /> }
    </MenuToggle>
  );
};

const MultipleMenuToggle = ({
  toggleRef,
  selected,
  isOpen,
  isDisabled,
  onToggleClick,
  onSelect,
  onClear,
  placeholder
}) => {
  return (
    <MenuToggle
      ref={toggleRef}
      onClick={onToggleClick}
      isExpanded={isOpen}
      isDisabled={isDisabled}
      className={classNames(styles.menuToggle)}
    >
      { _.isEmpty(selected) && <Placeholder value={placeholder} /> }
      { !_.isEmpty(selected) && <ChipGroup aria-label="Current selections"
        isClosable
        onClick={onClear}
      >
        { selected.map((item, index) =>
          (<Chip key={index}
            onClick={ev => {
              ev.stopPropagation();
              onSelect(ev, item);
            }}
          >
            { item.text }
          </Chip>)) }
      </ChipGroup>
      }
    </MenuToggle>);
};

const SingleTypeaheadMenuToggle = ({
  toggleRef, selected, inputValue, placeholder, onTextInputChange,
  onInputKeyDown, isOpen, isDisabled, onToggleClick, textInputRef, onClear }) => {

  const showValue = inputValue ?? _.first(selected)?.text;
  // TODO: Add placeholder
  return (
    <MenuToggle
      ref={toggleRef}
      variant="typeahead"
      onClick={onToggleClick}
      isExpanded={isOpen}
      isFullWidth
      isDisabled={isDisabled}
      className={classNames(styles.menuToggle)}
    >
      <TextInputGroup isPlain>
        <TextInputGroupMain
          value={showValue}
          onClick={onToggleClick}
          onChange={onTextInputChange}
          onKeyDown={onInputKeyDown}
          id="typeahead-select-input"
          autoComplete="off"
          innerRef={textInputRef}
          placeholder={placeholder}
          {...(showValue && {
            'aria-activedescendant': showValue
          })}
          role="combobox"
          isExpanded={isOpen}
          aria-controls="select-typeahead-listbox"
        />

        <TextInputGroupUtilities>
          { showValue && <Button variant="plain" onClick={onClear} aria-label="Clear input value">
            <Icons.Close aria-hidden />
          </Button> }
        </TextInputGroupUtilities>
      </TextInputGroup>
    </MenuToggle>);
};

const MultipleTypeaheadMenuToggle = ({
  toggleRef,
  selected,
  placeholder,
  inputValue,
  onSelect,
  onClear,
  onTextInputChange,
  onInputKeyDown,
  isOpen,
  isDisabled,
  onToggleClick,
  textInputRef
}) => {
  return (<MenuToggle
    role="combobox"
    variant="typeahead"
    onClick={onToggleClick}
    innerRef={toggleRef}
    isExpanded={isOpen}
    isFullWidth
    isDisabled={isDisabled}
    className={classNames(styles.menuToggle)}
  >
    <TextInputGroup isPlain>
      <TextInputGroupMain
        value={inputValue}
        onClick={onToggleClick}
        onChange={onTextInputChange}
        onKeyDown={onInputKeyDown}
        id="multi-typeahead-select-input"
        autoComplete="off"
        innerRef={textInputRef}
        placeholder={placeholder}
        // {...(activeItem && {
        //   'aria-activedescendant': activeItem
        // })}
        role="combobox"
        isExpanded={isOpen}
        aria-controls="select-multi-typeahead-listbox"
      >
        <ChipGroup aria-label="Current selections">
          { selected.map((item, index) =>
            (<Chip key={index}
              onClick={ev => {
                ev.stopPropagation();
                onSelect(ev, item);
              }}
            >
              { item.text }
            </Chip>)) }
        </ChipGroup>
      </TextInputGroupMain>
      <TextInputGroupUtilities>
        { selected.length > 0
          && <Button variant="plain"
            onClick={onClear}
            aria-label="Clear input value"
          >
            <Icons.Close aria-hidden />
          </Button> }
      </TextInputGroupUtilities>
    </TextInputGroup>
  </MenuToggle>);
};

export const useMenuToggle = (
  { isMultiple,
    isTypeahead,
    isOpen,
    selected,
    placeholder,
    isDisabled,
    // direction = 'down',
    inputValue,
    setInputValue,
    textInputRef,
    onToggleClick,
    onSelect,
    onInputKeyDown,
    onClear
  }) => {
  const onTextInputChange = (_event, value) => {
    setInputValue(value);
  };

  let toggle;
  if (!isMultiple && !isTypeahead) {
    toggle = toggleRef => (<SingleMenuToggle
      toggleRef={toggleRef}
      selected={selected}
      isOpen={isOpen}
      isDisabled={isDisabled}
      onToggleClick={onToggleClick}
      placeholder={placeholder}
    />);
  } else if (isMultiple && !isTypeahead) {
    toggle = toggleRef => (<MultipleMenuToggle
      toggleRef={toggleRef}
      selected={selected}
      isOpen={isOpen}
      isDisabled={isDisabled}
      onToggleClick={onToggleClick}
      onSelect={onSelect}
      onClear={onClear}
      placeholder={placeholder}
    />);
  } else if (isTypeahead && !isMultiple) {
    toggle = toggleRef => (<SingleTypeaheadMenuToggle
      toggleRef={toggleRef}
      selected={selected}
      inputValue={inputValue}
      onTextInputChange={onTextInputChange}
      onInputKeyDown={onInputKeyDown}
      isOpen={isOpen}
      placeholder={placeholder}
      isDisabled={isDisabled}
      onToggleClick={onToggleClick}
      textInputRef={textInputRef}
      onClear={onClear}
    />);
  } else if (isTypeahead && isMultiple) {
    toggle = toggleRef => (<MultipleTypeaheadMenuToggle
      toggleRef={toggleRef}
      selected={selected}
      inputValue={inputValue}
      onTextInputChange={onTextInputChange}
      onInputKeyDown={onInputKeyDown}
      isOpen={isOpen}
      placeholder={placeholder}
      isDisabled={isDisabled}
      onSelect={onSelect}
      onToggleClick={onToggleClick}
      textInputRef={textInputRef}
      onClear={onClear}
    />);
  }

  return ({
    menuToggle: toggle
  });
};
